import { z } from 'zod';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';

/**
 * ADR-159 §5 — End Employment: the ONE person verb behind the three person
 * buttons (Resign, Hire Withdraw, Mark No Show — ADR-149, ADR-158). It never
 * names a contract: the backend applies it to every contract the person holds
 * (with one contract, exactly the legs these buttons always sent). Whether a
 * scheduled end is withdrawn first is decided there too, from each contract's
 * state, so `withdrawScheduledEnd` is no longer sent.
 */
export const END_EMPLOYMENT_KINDS = Object.freeze({
  RESIGN: 'RESIGN',
  HIRE_WITHDRAW: 'HIRE_WITHDRAW',
  NO_SHOW: 'NO_SHOW'
});

const endEmploymentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(END_EMPLOYMENT_KINDS.RESIGN),
    employeeId: z.uuid(),
    resignationDate: z.iso.date('Resignation date must be in ISO format (YYYY-MM-DD)'),
    resignationReason: z.string().min(1, 'Resignation reason is required')
  }),
  z.object({
    kind: z.literal(END_EMPLOYMENT_KINDS.HIRE_WITHDRAW),
    employeeId: z.uuid(),
    reason: z.string().min(1, 'Reason is required')
  }),
  z.object({
    kind: z.literal(END_EMPLOYMENT_KINDS.NO_SHOW),
    employeeId: z.uuid(),
    plannedStart: z.iso.date('Planned start must be in ISO format (YYYY-MM-DD)').optional(),
    reason: z.string().optional()
  })
]);

export const endEmployment = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = endEmploymentSchema.parse(payload);
  const body = Object.fromEntries(Object.entries(p).filter(([, value]) => value !== undefined && value !== ''));

  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{
      type: EMPLOYMENT_EVENT_TYPES.PERSON_EMPLOYMENT_ENDED,
      payload: body
    }]
  });
};

const voidContractSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: z.uuid(),
  reason: z.string().min(1, 'Reason is required')
});

/**
 * ADR-159 §4.1 (C5) — Void Contract: ONE contract that has not started is
 * voided while the person keeps another. Always names the contract.
 */
export const voidContract = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = voidContractSchema.parse(payload);
  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{ type: EMPLOYMENT_EVENT_TYPES.CONTRACT_VOIDED, payload: p }]
  });
};
